// i18n: translated
/**
 * テナント向けお知らせバナー (steering 488)
 *
 * `AppLayout` の `<main>` 最上部 (ページ本文の直前) に描画される。
 *
 * 表示条件 (fail-closed):
 *   - surface が tenant admin (`app`) のときだけ。**portal / ops には出さない**
 *   - 認証済みユーザーがいるときだけ
 *
 * `AppLayout` は app / portal / ops の 3 surface で共通のため、この 2 段ガードが
 * 無いと ops コンソールや依頼者ポータルにもテナント向けバナーが出てしまう。
 *
 * 取得失敗は **fail-soft** — バナーを描画せず、エラー表示もしない。
 * 運営のお知らせが取れないことで業務画面を壊さない。
 */
import { useMemo, useState } from 'react';
import { useT } from '@/i18n';
import { commonNs } from '@/i18n/namespaces/common';
import { shellNs } from '@/i18n/namespaces/shell';
import { useAuth } from '@/hooks/useAuth';
import { useActiveAnnouncements } from '@/hooks/useAnnouncements';
import {
  getAnnouncementContainerClass,
  getAnnouncementLevelStyle,
} from '@/utils/announcementStyles';
import { isDismissed, markDismissed, readDismissedKeys } from '@/utils/announcementDismissal';
import type { ActiveAnnouncement } from '@/types';

/**
 * surface 判定 (banner 用の最小 local predicate)。
 *
 * `App.tsx` の `detectSurfaceMode` / `Sidebar.tsx` の `IS_OPS_SURFACE` と
 * **同一規則**をローカルに持つ。既存 2 箇所が import 循環回避のために意図的な
 * 重複になっており、本 feature のために共有 util へ切り出す横断 refactor は
 * scope を広げるため行わない (Mgr Gate 1 Q2 裁定)。
 *
 * **判定値が既存ロジックと一致すること**を `AnnouncementBanner.test.tsx` で固定する。
 * 将来 surface 判定を整理する場合は独立した refactor として行う。
 */
export function isTenantAdminSurface(hostname: string): boolean {
  if (/^(dev-)?ops\./.test(hostname)) return false;
  if (hostname.includes('portal')) return false;
  return true;
}

/** level ごとの semantic アイコン (Heroicons 準拠、24x24 / stroke-width 2)。 */
function AnnouncementIcon({ level, className }: { level: string; className: string }) {
  const common = {
    className,
    fill: 'none',
    viewBox: '0 0 24 24',
    strokeWidth: 2,
    stroke: 'currentColor',
    'aria-hidden': true,
  } as const;

  if (level === 'success') {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    );
  }
  if (level === 'warning') {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
        />
      </svg>
    );
  }
  if (level === 'error') {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
      />
    </svg>
  );
}

interface BannerRowProps {
  announcement: ActiveAnnouncement;
  onDismiss: (announcement: ActiveAnnouncement) => void;
}

function BannerRow({ announcement, onDismiss }: BannerRowProps) {
  // 閉じるボタンの文言だけ表示言語に合わせる (お知らせの本文は運営が書いたデータなので訳さない)
  const t = useT(shellNs);
  const tc = useT(commonNs);
  const levelStyle = getAnnouncementLevelStyle(announcement.level);
  const container = getAnnouncementContainerClass(announcement.level, announcement.style);

  return (
    <div
      role="status"
      className={`flex items-start gap-3 rounded-lg px-4 py-3 ${container}`}
      data-testid="announcement-banner"
      data-level={announcement.level}
      data-style={announcement.style}
    >
      <AnnouncementIcon
        level={announcement.level}
        className={`mt-0.5 h-5 w-5 shrink-0 ${levelStyle.icon}`}
      />

      <div className={`min-w-0 flex-1 text-sm ${levelStyle.text}`}>
        <span className="font-semibold">{announcement.title}</span>
        <span className="ml-2 break-words">{announcement.message}</span>
      </div>

      {announcement.linkLabel && announcement.linkUrl ? (
        <a
          href={announcement.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`shrink-0 whitespace-nowrap text-sm font-medium underline-offset-2 hover:underline ${levelStyle.link}`}
        >
          {announcement.linkLabel}
        </a>
      ) : null}

      <button
        type="button"
        onClick={() => onDismiss(announcement)}
        aria-label={t('announcement.dismiss', { title: announcement.title })}
        title={tc('actions.close')}
        className={`shrink-0 rounded p-0.5 transition-colors ${levelStyle.close}`}
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function AnnouncementBanner() {
  const { user } = useAuth();

  // surface + 認証の 2 段ガード。どちらか欠けたら fetch も描画もしない
  const isTenantAdmin = isTenantAdminSurface(window.location.hostname);
  const shouldFetch = isTenantAdmin && !!user;

  const { data, error } = useActiveAnnouncements(shouldFetch);
  const [dismissedKeys, setDismissedKeys] = useState<string[]>(() =>
    shouldFetch ? readDismissedKeys() : [],
  );

  const visible = useMemo(() => {
    if (!shouldFetch || error || !data?.items) return [];
    // BE が優先度順・上限件数で返すため、FE は並べ替えず dismissal だけを適用する
    return data.items.filter((a) => !isDismissed(a.announcementId, a.updatedAt, dismissedKeys));
  }, [shouldFetch, error, data, dismissedKeys]);

  // 0 件のときは要素そのものを出さない (空の余白でレイアウトを動かさない)
  if (visible.length === 0) return null;

  const handleDismiss = (announcement: ActiveAnnouncement) => {
    setDismissedKeys(markDismissed(announcement.announcementId, announcement.updatedAt));
  };

  return (
    <div className="flex flex-col gap-2 px-6 pt-4" data-testid="announcement-banner-list">
      {visible.map((a) => (
        <BannerRow key={a.announcementId} announcement={a} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
